import React, { useMemo, useState } from 'react';
import { Box, Collapse } from '@mui/material';
import { Text, textLevel, fontWeight, colors, Icons, Link, linkType } from '../../../../../../packages';

const ExerciseSteps = ({ exerciseSteps }) => {
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  const toggleStep = stepId => {
    const newExpandedSteps = new Set(expandedSteps);
    if (newExpandedSteps.has(stepId)) {
      newExpandedSteps.delete(stepId);
    } else {
      newExpandedSteps.add(stepId);
    }
    setExpandedSteps(newExpandedSteps);
  };

  // Process exerciseSteps to create hierarchical structure with unlimited nesting
  const processedExerciseSteps = blocks => {
    if (!blocks || blocks.length === 0) return [];

    const blockMap = new Map();
    const rootSteps = [];

    // First pass: create a map of all blocks by cbId with substeps array
    blocks.forEach(block => {
      blockMap.set(block.cbId, {
        ...block,
        substeps: [],
      });
    });

    // Second pass: build the hierarchy
    // stepId points to the cbId of the parent step
    blocks.forEach(block => {
      const currentBlock = blockMap.get(block.cbId);

      if (block.stepId && block.stepId !== null && block.stepId !== undefined) {
        // This block has a parent (stepId points to parent's cbId)
        const parentBlock = blockMap.get(block.stepId);
        if (parentBlock) {
          parentBlock.substeps.push(currentBlock);
        } else {
          // Parent not found, treat as root level
          console.warn(`Parent block with cbId ${block.stepId} not found for block ${block.cbId}`);
          rootSteps.push(currentBlock);
        }
      } else {
        // This is a root level step (no stepId or stepId is null/undefined)
        rootSteps.push(currentBlock);
      }
    });

    // Sort substeps recursively to maintain order
    const sortSubsteps = steps => {
      steps.forEach(step => {
        if (step.substeps && step.substeps.length > 0) {
          step.substeps.sort((a, b) => {
            // Sort by order if available, otherwise by cbId
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return a.cbId.localeCompare(b.cbId);
          });
          sortSubsteps(step.substeps);
        }
      });
    };

    // Sort root steps
    rootSteps.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return a.cbId.localeCompare(b.cbId);
    });

    sortSubsteps(rootSteps);

    return rootSteps;
  };

  const processedSteps = useMemo(() => {
    return processedExerciseSteps(exerciseSteps);
  }, [exerciseSteps]);
  // Flatten the hierarchy into a single list with hierarchical numbering
  const flattenSteps = (steps, parentNumber = '') => {
    const flattened = [];

    steps.forEach((step, index) => {
      const stepNumber = parentNumber ? `${parentNumber}.${index + 1}` : `${index + 1}`;
      // Add the current step
      flattened.push({
        ...step,
        stepNumber,
      });

      // Recursively add substeps
      if (step.substeps && step.substeps.length > 0) {
        const nestedSteps = flattenSteps(step.substeps, stepNumber);
        flattened.push(...nestedSteps);
      }
    });

    return flattened;
  };

  const flattenedSteps = useMemo(() => {
    return flattenSteps(processedSteps);
  }, [processedSteps]);

  const renderStep = step => {
    const stepName = step.basicInfo?.name || '';
    const stepText = step.basicInfo?.text || '';
    const isExpanded = expandedSteps.has(step.cbId);
    const hasContent = !!stepText;

    return (
      <Box
        key={step.cbId}
        display="flex"
        flexDirection="column"
        marginBottom="8px"
        backgroundColor={colors.primary25}
        borderRadius="8px"
        padding="16px"
      >
        {/* Step Header */}
        <Box display="flex" alignItems="center" gap="4px">
          <Text
            level={textLevel.M}
            weight={fontWeight.SEMI_BOLD}
            color={colors.neutral900}
            minWidth="fit-content"
          >
            {step.stepNumber}.
          </Text>
          <Box display="flex" alignItems="center" justifyContent="space-between" flex="1">
            <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
              {stepName}
            </Text>
            {hasContent && (
              <Link variant={linkType.CALLBACK} onClick={() => toggleStep(step.cbId)}>
                {isExpanded ? 'Hide script' : 'Show script'}
              </Link>
            )}
          </Box>
        </Box>
        {/* Step Script - Collapsible */}
        {hasContent && (
          <Collapse in={isExpanded}>
            <Box display="flex" flexDirection="column" gap="8px" paddingTop="12px">
              <Box display="flex" alignItems="center" gap="8px">
                <Icons glyph="chat" color={colors.primary500} width="16px" height="16px" />
                <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.primary500}>
                  Script:
                </Text>
              </Box>
              <Text level={textLevel.M} weight={fontWeight.REGULAR} color={colors.neutral900}>
                {stepText}
              </Text>
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  if (!exerciseSteps || exerciseSteps.length === 0) {
    return (
      <Box display="flex" justifyContent="center" padding="24px">
        <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral500}>
          No exercise steps available
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      {flattenedSteps.map(step => renderStep(step))}
    </Box>
  );
};

export default ExerciseSteps;
